// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/RulesPanel.test.tsx
import { MagnifyingGlass, UploadSimple, X } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { RuleDetailPanel } from "./RuleDetailPanel";

import { useUI } from "../../../../../contexts/ui";
import { canManageKnowledgeDatabase } from "../../../../../utils/auth";
import { debounce } from "../../../../../utils/debounce";
import { isNil } from "../../../../../utils/fp";
import { errorMessage } from "../../../../../utils/http";
import {
  KnowledgeRuleCategory,
  KnowledgeRuleStatus,
  isSourceInFlight,
  type KnowledgeRuleResult,
} from "../../../../../utils/parsers/knowledge";
import {
  useArchiveRuleMutation,
  useKnowledgeRulesQuery,
  useKnowledgeSourcesQuery,
  useLoadMoreKnowledgeRulesMutation,
  usePromoteRuleMutation,
  useRejectRuleMutation,
} from "../../../../../utils/queries/knowledge";
import { knowledgeDatabaseRoute, type RuleStatusFilter } from "../route";

const PAGE_SIZE = 50;

/** Route filter value -> API status value. "suggestions" is plural in the URL
 * for readability; the API status enum is singular. */
function apiStatus(filter: RuleStatusFilter): string {
  return filter === "suggestions" ? KnowledgeRuleStatus.SUGGESTION : filter;
}

const CATEGORY_LABELS: Record<KnowledgeRuleCategory, string> = {
  [KnowledgeRuleCategory.CLAIM]: "Claim",
  [KnowledgeRuleCategory.BRAND]: "Brand",
  [KnowledgeRuleCategory.CHECKLIST]: "Checklist",
  [KnowledgeRuleCategory.POLICY]: "Policy",
  [KnowledgeRuleCategory.OTHER]: "Other",
};

function RulesEmptyState({
  hasSources,
  isExtracting,
  onGoToSources,
}: {
  hasSources: boolean;
  isExtracting: boolean;
  onGoToSources: () => void;
}) {
  if (!hasSources) {
    return (
      <Flex
        direction="column"
        align="center"
        gap="2"
        className="py-12"
        data-testid="knowledge-rules-empty"
      >
        <Text size="2" color="gray">
          No rules yet. Upload your guideline documents and Puntt will extract
          rules with citations back to the exact page or cell.
        </Text>
        <Button
          size="1"
          onClick={onGoToSources}
          data-testid="knowledge-rules-empty-upload-cta"
        >
          <UploadSimple aria-hidden="true" />
          Upload guidelines
        </Button>
      </Flex>
    );
  }
  if (isExtracting) {
    return (
      <Flex
        direction="column"
        align="center"
        gap="2"
        className="py-12"
        data-testid="knowledge-rules-empty-extracting"
      >
        <Text size="2" color="gray">
          Rules are being extracted from your documents — they will appear here
          shortly.
        </Text>
      </Flex>
    );
  }
  return (
    <Flex
      direction="column"
      align="center"
      gap="2"
      className="py-12"
      data-testid="knowledge-rules-empty"
    >
      <Text size="2" color="gray">
        No rules matched. Try a different filter, or upload more guideline
        documents.
      </Text>
    </Flex>
  );
}

/**
 * Rules tab: status filter chips + search, source-scoped filtering (arriving
 * from a source row's "N rules" link), admin transitions on suggestions
 * (promote/reject) and active rules (archive), and a deep-linkable detail
 * panel with trust-ordered citations.
 */
export function RulesPanel() {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const { status, category, q, ruleId, sourceId } =
    knowledgeDatabaseRoute.useSearch();
  const navigate = knowledgeDatabaseRoute.useNavigate();
  const canManage = canManageKnowledgeDatabase();

  const [query, setQuery] = useState<string | undefined>(q);

  const rulesQuery = useKnowledgeRulesQuery({
    status: apiStatus(status),
    category,
    query,
    sourceId,
    pageSize: PAGE_SIZE,
  });
  const sourcesQuery = useKnowledgeSourcesQuery();
  const loadMore = useLoadMoreKnowledgeRulesMutation();
  const promote = usePromoteRuleMutation();
  const reject = useRejectRuleMutation();
  const archive = useArchiveRuleMutation();

  const [extra, setExtra] = useState<KnowledgeRuleResult[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [more, setMore] = useState(false);

  useEffect(() => {
    setExtra([]);
    setCursor(rulesQuery.data?.nextCursor ?? null);
    setMore(rulesQuery.data?.more ?? false);
  }, [rulesQuery.data]);

  const rules = useMemo(
    () => [...(rulesQuery.data?.rules ?? []), ...extra],
    [rulesQuery.data, extra],
  );
  const statusCounts = rulesQuery.data?.statusCounts ?? {};
  const categoryCounts = rulesQuery.data?.categoryCounts ?? {};
  const sources = sourcesQuery.data?.sources ?? [];
  const filterSource = sourceId
    ? sources.find((s) => s._id === sourceId)
    : undefined;
  const activeRule = ruleId ? rules.find((r) => r._id === ruleId) : undefined;

  const handleSearch = useMemo(
    () => debounce((next: string) => setQuery(next.trim() || undefined)),
    [],
  );

  function setStatusFilter(next: RuleStatusFilter) {
    navigate({
      search: (prev) => ({ ...prev, status: next, ruleId: undefined }),
    });
  }

  /** Toggle semantics: clicking the active category chip clears the filter. */
  function setCategoryFilter(next: KnowledgeRuleCategory) {
    navigate({
      search: (prev) => ({
        ...prev,
        category: prev.category === next ? undefined : next,
        ruleId: undefined,
      }),
    });
  }

  function openRule(nextRuleId: string | undefined) {
    navigate({ search: (prev) => ({ ...prev, ruleId: nextRuleId }) });
  }

  function clearSourceFilter() {
    navigate({
      search: (prev) => ({ ...prev, sourceId: undefined, ruleId: undefined }),
    });
  }

  async function runTransition(
    mutation:
      | ReturnType<typeof usePromoteRuleMutation>
      | ReturnType<typeof useRejectRuleMutation>
      | ReturnType<typeof useArchiveRuleMutation>,
    id: string,
    failureTitle: string,
  ) {
    mutation.mutate(id, {
      async onError(error) {
        notify({
          title: failureTitle,
          message: await errorMessage(error),
          variant: "error",
        });
      },
    });
  }

  const statusChip = (filter: RuleStatusFilter, label: string) => {
    const count = statusCounts[apiStatus(filter)];
    const active = status === filter;
    return (
      <button
        type="button"
        onClick={() => setStatusFilter(filter)}
        aria-pressed={active}
        aria-label={`Show ${label} rules`}
        className="cursor-pointer rounded-full border-none bg-transparent p-0"
        data-testid={`knowledge-rules-status-${filter}`}
      >
        <Badge color="gray" variant={active ? "solid" : "soft"}>
          {label}
          {count != null ? ` (${count})` : ""}
        </Badge>
      </button>
    );
  };

  // Chips for every category present in the scoped set (counts ignore the
  // active category filter, so switching between categories never dead-ends);
  // the active category stays visible even at count 0 so it can be cleared.
  const visibleCategories = Object.values(KnowledgeRuleCategory).filter(
    (cat) => (categoryCounts[cat] ?? 0) > 0 || category === cat,
  );

  const categoryChip = (cat: KnowledgeRuleCategory) => {
    const count = categoryCounts[cat] ?? 0;
    const active = category === cat;
    return (
      <button
        key={cat}
        type="button"
        onClick={() => setCategoryFilter(cat)}
        aria-pressed={active}
        aria-label={
          active
            ? `Stop filtering by ${CATEGORY_LABELS[cat]} rules`
            : `Show only ${CATEGORY_LABELS[cat]} rules`
        }
        className="cursor-pointer rounded-full border-none bg-transparent p-0"
        data-testid={`knowledge-rules-category-${cat}`}
      >
        <Badge color="blue" variant={active ? "solid" : "soft"}>
          {CATEGORY_LABELS[cat]} ({count})
        </Badge>
      </button>
    );
  };

  const showEmpty = !rulesQuery.isLoading && rules.length === 0;
  const hasSources = sources.length > 0;
  const isExtracting = sources.some(isSourceInFlight);

  return (
    <section
      aria-label={t("settings.rulesPanel.knowledgeRules")}
      data-testid="knowledge-rules-panel"
    >
      <Flex gap="2" align="center" wrap="wrap" className="mb-3">
        {statusChip("active", "Active")}
        {statusChip("suggestions", "Suggestions")}
        {statusChip("archived", "Archived")}
        <TextField.Root
          placeholder={t("settings.rulesPanel.searchRules")}
          size="1"
          variant="surface"
          aria-label={t("settings.rulesPanel.searchRules")}
          defaultValue={q}
          onInput={({ target }) => {
            handleSearch((target as HTMLInputElement).value);
          }}
          data-testid="knowledge-rules-search-input"
        >
          <TextField.Slot side="right">
            <MagnifyingGlass />
          </TextField.Slot>
        </TextField.Root>
        {filterSource ? (
          <Badge
            color="blue"
            variant="soft"
            data-testid="knowledge-rules-source-filter-chip"
          >
            From: {filterSource.file.fileName}
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              aria-label={t("settings.rulesPanel.clearSourceFilter")}
              onClick={clearSourceFilter}
              data-testid="knowledge-rules-source-filter-clear"
            >
              <X aria-hidden="true" />
            </IconButton>
          </Badge>
        ) : null}
      </Flex>

      {visibleCategories.length > 0 ? (
        <Flex
          gap="2"
          align="center"
          wrap="wrap"
          className="mb-3"
          aria-label={t("settings.rulesPanel.filterRulesByCategory")}
          data-testid="knowledge-rules-category-chips"
        >
          {visibleCategories.map(categoryChip)}
        </Flex>
      ) : null}

      {showEmpty ? (
        <RulesEmptyState
          hasSources={hasSources}
          isExtracting={isExtracting}
          onGoToSources={() =>
            navigate({ search: (prev) => ({ ...prev, tab: "sources" }) })
          }
        />
      ) : (
        <Table.Root data-testid="knowledge-rules-table">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>
                {t("settings.rulesPanel.rule")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                {t("settings.rulesPanel.category")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                {t("settings.rulesPanel.scope")}
              </Table.ColumnHeaderCell>
              {status === "suggestions" ? (
                <Table.ColumnHeaderCell>
                  {t("settings.rulesPanel.confidence")}
                </Table.ColumnHeaderCell>
              ) : null}
              <Table.ColumnHeaderCell>
                {t("settings.rulesPanel.sources")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                {t("settings.rulesPanel.usedReviews")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                {t("settings.rulesPanel.updated")}
              </Table.ColumnHeaderCell>
              {canManage ? (
                <Table.ColumnHeaderCell
                  aria-label={t("common:labels.actions")}
                />
              ) : null}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rules.map((rule) => (
              <Table.Row
                key={rule._id}
                onClick={() => openRule(rule._id)}
                className="cursor-pointer"
                data-testid={`knowledge-rules-row-${rule._id}`}
              >
                <Table.Cell>
                  <Text size="2">{rule.title ?? rule.content}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge color="gray" variant="soft">
                    {rule.category}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Flex gap="1" wrap="wrap">
                    {[rule.scope.brand, rule.scope.region, rule.scope.team]
                      .filter(Boolean)
                      .map((dim) => (
                        <Badge key={dim} color="gray" variant="soft">
                          {dim}
                        </Badge>
                      ))}
                  </Flex>
                </Table.Cell>
                {status === "suggestions" ? (
                  <Table.Cell>
                    <Badge
                      color="amber"
                      variant="soft"
                      data-testid={`knowledge-rules-confidence-${rule._id}`}
                    >
                      {Math.round(rule.confidence * 100)}%
                    </Badge>
                  </Table.Cell>
                ) : null}
                <Table.Cell>
                  <Text size="1">{rule.citations.length}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text
                    size="1"
                    color="gray"
                    data-testid={`knowledge-rules-usage-${rule._id}`}
                  >
                    {rule.usage.reviewCount}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="1" color="gray">
                    {new Date(rule.updatedAt).toLocaleDateString()}
                  </Text>
                </Table.Cell>
                {canManage ? (
                  <Table.Cell onClick={(event) => event.stopPropagation()}>
                    <Flex gap="2">
                      {rule.status === KnowledgeRuleStatus.SUGGESTION ? (
                        <>
                          <Button
                            size="1"
                            variant="soft"
                            color="green"
                            loading={promote.isPending}
                            onClick={() =>
                              runTransition(
                                promote,
                                rule._id,
                                "Could not promote rule",
                              )
                            }
                            data-testid={`knowledge-rules-promote-${rule._id}`}
                          >
                            {t("settings.rulesPanel.promote")}
                          </Button>
                          <Button
                            size="1"
                            variant="soft"
                            color="red"
                            loading={reject.isPending}
                            onClick={() =>
                              runTransition(
                                reject,
                                rule._id,
                                "Could not reject rule",
                              )
                            }
                            data-testid={`knowledge-rules-reject-${rule._id}`}
                          >
                            {t("settings.rulesPanel.reject")}
                          </Button>
                        </>
                      ) : null}
                      {rule.status === KnowledgeRuleStatus.ACTIVE ? (
                        <Button
                          size="1"
                          variant="soft"
                          color="gray"
                          loading={archive.isPending}
                          onClick={() =>
                            runTransition(
                              archive,
                              rule._id,
                              "Could not archive rule",
                            )
                          }
                          data-testid={`knowledge-rules-archive-${rule._id}`}
                        >
                          {t("settings.rulesPanel.archive")}
                        </Button>
                      ) : null}
                    </Flex>
                  </Table.Cell>
                ) : null}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      {more ? (
        <Flex justify="center" className="mt-3">
          <Button
            size="1"
            variant="soft"
            color="gray"
            loading={loadMore.isPending}
            onClick={() => {
              if (isNil(cursor)) return;
              loadMore.mutate(
                {
                  status: apiStatus(status),
                  category,
                  query,
                  sourceId,
                  pageSize: PAGE_SIZE,
                  searchAfter: cursor,
                },
                {
                  onSuccess(data) {
                    // parseResponse yields undefined on a malformed payload
                    // (already console-logged) — keep the current page.
                    if (isNil(data)) return;
                    setExtra((prev) => [...prev, ...data.rules]);
                    setCursor(data.nextCursor);
                    setMore(data.more);
                  },
                },
              );
            }}
            data-testid="knowledge-rules-load-more"
          >
            {t("settings.rulesPanel.loadMore")}
          </Button>
        </Flex>
      ) : null}

      <RuleDetailPanel
        rule={activeRule}
        sources={sources}
        onClose={() => openRule(undefined)}
      />
    </section>
  );
}
